import React, { useState, useEffect } from 'react';
import { X, Clock, CheckCircle, XCircle, AlertTriangle, Code } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTenantProtection } from '../../../hooks/useTenantProtection';

const ExecutionDetails = ({ execution, onClose }) => {
  const { getProtectedTenantCode } = useTenantProtection();
  
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, [execution]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get(
        `/api/automation/history/${execution.ExecutionID}/logs`,
        { headers: { 'X-Tenant-Code': tenantCode } }
      );
      setDetails(response.data);
    } catch (error) {
      console.error('Failed to load logs:', error);
      toast.error('Failed to load execution logs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'running':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getLogLevelIcon = (level) => {
    switch (level) {
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warn':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'info':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    return `${(ms / 60000).toFixed(2)}m`;
  };

  const formatDateTimeGMT7 = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatTimeGMT7 = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      timeZone: 'Asia/Bangkok',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatJSON = (value) => {
    if (!value) return null;
    try {
      if (typeof value === 'string') {
        return JSON.stringify(JSON.parse(value), null, 2);
      }
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    }
  };

  const executionData = details?.execution || {};
  const logs = details?.logs || [];
  const status = executionData.status ?? executionData.Status ?? execution.Status;
  const duration = executionData.duration ?? executionData.Duration ?? execution.Duration;
  const executionType = executionData.executionType ?? executionData.ExecutionType ?? execution.ExecutionType;
  const jobName = executionData.jobName ?? executionData.JobName ?? execution.JobName;
  const startTime = executionData.startTime ?? executionData.StartTime ?? execution.StartTime;
  const endTime = executionData.endTime ?? executionData.EndTime ?? execution.EndTime;
  const requestData = executionData.requestData ?? executionData.RequestData;
  const responseData = executionData.responseData ?? executionData.ResponseData;
  const errorMessage = executionData.errorMessage ?? executionData.ErrorMessage;
  const errorStack = executionData.errorStack ?? executionData.ErrorStack;
  const errorName = executionData.errorName ?? executionData.ErrorName;
  const errorDetails = executionData.errorDetails ?? executionData.ErrorDetails;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Execution Details</h3>
              <p className="mt-1 text-sm text-gray-500">
                Execution ID: {execution.ExecutionID}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Status</div>
                    <div className="flex items-center space-x-2">
                      <div className={`h-2 w-2 rounded-full ${getStatusColor(status)}`}></div>
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Duration</div>
                    <div className="text-sm font-medium text-gray-900">
                      {formatDuration(duration)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Type</div>
                    <div className="text-sm font-medium text-gray-900 capitalize">
                      {executionType}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Job</div>
                    <div className="text-sm font-medium text-gray-900">
                      {jobName}
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Start Time</div>
                    <div className="text-sm text-gray-900">
                      {formatDateTimeGMT7(startTime)}
                    </div>
                  </div>
                  {endTime && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">End Time</div>
                      <div className="text-sm text-gray-900">
                        {formatDateTimeGMT7(endTime)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Request Data */}
              {requestData && (
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Code className="h-4 w-4 text-gray-500" />
                    <h4 className="text-sm font-medium text-gray-900">Request Parameters</h4>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-xs text-green-400 font-mono">
                      {formatJSON(requestData)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Execution Logs */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Execution Log</h4>
                <div className="space-y-2">
                  {logs.length === 0 && (
                    <div className="text-sm text-gray-500">
                      No detailed logs available for this execution.
                    </div>
                  )}
                  {logs.map((log, index) => (
                    <div
                      key={index}
                      className={`flex items-start space-x-3 p-3 rounded-lg ${
                        log.level === 'error' ? 'bg-red-50' :
                        log.level === 'warn' ? 'bg-yellow-50' :
                        'bg-gray-50'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getLogLevelIcon(log.level)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-900">
                            {log.message}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatTimeGMT7(log.timestamp)}
                          </div>
                        </div>
                        {log.data && (
                          <div className="mt-2 bg-white rounded p-2 overflow-x-auto">
                            <pre className="text-xs text-gray-600 font-mono">
                              {JSON.stringify(log.data, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.stack && (
                          <div className="mt-2 bg-white rounded p-2 overflow-x-auto">
                            <pre className="text-xs text-red-600 font-mono whitespace-pre-wrap">
                              {log.stack}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Response Data */}
              {responseData && (
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <h4 className="text-sm font-medium text-gray-900">Response Data</h4>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-xs text-green-400 font-mono">
                      {formatJSON(responseData)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Error Details */}
              {errorMessage && (
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <h4 className="text-sm font-medium text-gray-900">Error Details</h4>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-red-800 mb-2">
                      {errorName && <span className="mr-2 text-xs uppercase tracking-wide">{errorName}</span>}
                      {errorMessage}
                    </div>
                    {errorDetails && (
                      <div className="bg-white rounded p-3 overflow-x-auto mb-3">
                        <pre className="text-xs text-red-600 font-mono whitespace-pre-wrap">
                          {formatJSON(errorDetails)}
                        </pre>
                      </div>
                    )}
                    {errorStack && (
                      <div className="bg-white rounded p-3 overflow-x-auto">
                        <pre className="text-xs text-red-600 font-mono whitespace-pre-wrap">
                          {errorStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecutionDetails;

